import { useMemo, useState } from "react"
import { Line, LineChart, Legend, ReferenceLine, Tooltip, XAxis, YAxis, ResponsiveContainer } from "recharts"
import { Text } from "@/components"
import { AttritionRate, CapTableFunding, CompanyEmployeeCount } from "@/types"
import { Box, HStack } from "@chakra-ui/react"
import { ChartRangeSelector } from "./ChartRangeSelector"
import { DateRange, DATE_RANGE_OPTIONS, filterBeforeFoundingDate, filterByDateRange } from "./chartUtils"
import { useFundraisingMarkers } from "./FundraisingMarkers"

interface Props {
  generalAttrition: AttritionRate[]
  salesAttrition: AttritionRate[]
  employeeCounts: CompanyEmployeeCount[]
  capTableFunding?: CapTableFunding[]
  foundingDate?: string | null
}

export const AttritionChart = ({ generalAttrition, salesAttrition, employeeCounts, capTableFunding = [], foundingDate }: Props) => {
  const [dateRange, setDateRange] = useState<DateRange>("all")

  const chartData = useMemo(() => {
    // Deduplicate all series to one point per calendar month before merging.
    // For headcount: take the max count per month (scrapes are partial; higher = more complete).
    // For attrition rates: take the last reading per month.
    const monthKey = (date: string) => date.slice(0, 7)

    const generalByMonth = new Map<string, number>()
    generalAttrition.forEach((d) => {
      generalByMonth.set(monthKey(d.date), d.rate * 100)
    })

    const salesByMonth = new Map<string, number>()
    salesAttrition.forEach((d) => {
      salesByMonth.set(monthKey(d.date), d.rate * 100)
    })

    const headcountByMonth = new Map<string, number>()
    employeeCounts.forEach((d) => {
      const key = monthKey(d.date)
      const existing = headcountByMonth.get(key)
      if (existing == null || d.count > existing) headcountByMonth.set(key, d.count)
    })

    const allMonths = new Set([
      ...generalByMonth.keys(),
      ...salesByMonth.keys(),
      ...headcountByMonth.keys(),
    ])

    const dateMap = new Map<string, { date: string; general?: number; sales?: number; headcount?: number }>()
    allMonths.forEach((mo) => {
      dateMap.set(mo, {
        date: mo,
        ...(generalByMonth.has(mo) ? { general: generalByMonth.get(mo) } : {}),
        ...(salesByMonth.has(mo) ? { sales: salesByMonth.get(mo) } : {}),
        ...(headcountByMonth.has(mo) ? { headcount: headcountByMonth.get(mo) } : {}),
      })
    })

    const sorted = Array.from(dateMap.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    )
    return filterByDateRange(filterBeforeFoundingDate(sorted, foundingDate), dateRange)
  }, [generalAttrition, salesAttrition, employeeCounts, dateRange, foundingDate])

  const fundraisingMarkers = useFundraisingMarkers(capTableFunding, chartData.map((d) => d.date))

  if (chartData.length === 0) {
    return <Text color="text.muted">No attrition data available.</Text>
  }

  return (
    <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
      <HStack mb={4} justifyContent="space-between" alignItems="center">
        <Text fontSize="lg" fontWeight="bold">
          Attrition
        </Text>
        <ChartRangeSelector options={DATE_RANGE_OPTIONS} value={dateRange} onChange={setDateRange} />
      </HStack>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={chartData}>
          <XAxis dataKey="date" fontSize={12} />
          <YAxis yAxisId="left" allowDecimals={false} fontSize={12} unit="%" />
          <YAxis yAxisId="right" orientation="right" fontSize={12} />
          <Tooltip contentStyle={{ backgroundColor: "var(--chakra-colors-card-bg)" }} />
          <Legend />
          {fundraisingMarkers.map((m, i) => (
            <ReferenceLine key={`fr-${i}`} x={m.x} stroke="#A0AEC0" strokeDasharray="4 4" label={{ value: m.label, position: "top", fontSize: 10, fill: "#718096" }} />
          ))}
          <Line yAxisId="left" type="monotone" dataKey="general" name="General Attrition" stroke="#DD6B20" dot={false} connectNulls />
          <Line yAxisId="left" type="monotone" dataKey="sales" name="Sales Attrition" stroke="#E53E3E" dot={false} connectNulls />
          <Line yAxisId="right" type="monotone" dataKey="headcount" name="Head Count" stroke="#38A169" dot={false} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}
